import {
  EllipsisVertical,
  Eye,
  EyeOff,
  Info,
  RefreshCcw,
  Trash2,
  X,
} from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { ExportHistoryType, InvoiceData } from "@/apis/gql/types";
import { InvoiceExportAlert } from "@/components/shared/alerts/invoice-export-alert";
import { StatusSelect } from "@/components/shared/select-inputs/status-select";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TypographySmall } from "@/components/ui/typography";
import { InvoiceType } from "@/lib/constants/invoice-type";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { cn } from "@/lib/utils";
import { useUpdateInvoice } from "@/queries/invoices/update";

import { ReviewModes } from "../coreInvoiceForm/invoiceCoreInfoForm";

import { ProcessingStates } from "@/components/shared/miscellaneous/processing-states";
import { ActivityModeToggle } from "./activity-mode-toggle";
import { InvoiceErrorAlert } from "./invoice-error-alert";
import {
  InvoiceTypeDropdown,
  type InvoiceTypeOption,
} from "./invoice-type-dropdown";
import { OcrReviewMode, type OcrReviewModes } from "./ocr-review-mode";

type Props = {
  invoice: InvoiceData & { exports: ExportHistoryType[] };
  reviewMode: ReviewModes;
  showActivity: boolean;
  ocrReviewMode: OcrReviewModes;
  setShowActivity: (show: boolean) => void;
  setOcrReviewMode: (ocrReviewMode: OcrReviewModes) => void;
  onRetryExtraction: () => Promise<any>;
  onDelete: (invoiceId: InvoiceData["invoiceId"]) => void;
  onUpdate: (status: InvoiceData["status"]) => void;
  onReviewModeChange: (reviewMode: ReviewModes) => void;
  onRemoveBooking: () => void;
  disabled?: boolean;
};

export const InvoiceStatusBanner = ({
  invoice,
  reviewMode,
  showActivity,
  ocrReviewMode,
  disabled = false,
  setShowActivity,
  setOcrReviewMode,
  onDelete,
  onUpdate,
  onRetryExtraction,
  onReviewModeChange,
  onRemoveBooking,
}: Props) => {
  const { t } = useTranslation();
  const [updateInvoice] = useUpdateInvoice();

  const handleDelete = useCallback(() => {
    onDelete(invoice.invoiceId);
  }, [onDelete, invoice.invoiceId]);

  const handleUpdateStatus = useCallback((status: InvoiceData["status"]) => {
    onUpdate(status);
  }, []);

  const handleTypeSelect = useCallback(
    async (option: InvoiceTypeOption) => {
      try {
        if (option === "no_booking") {
          await updateInvoice({
            invoiceId: invoice.invoiceId,
            isBooking: false,
            status: InvoiceStatus.REVIEWED,
          });
          onRemoveBooking();
        } else {
          const typeChanged = option !== invoice.type;
          await updateInvoice({
            invoiceId: invoice.invoiceId,
            isBooking: true,
            ...(typeChanged && { type: option, supplierId: null }),
          });
          toast.success(t("generic_success"));
        }
      } catch (error) {
        toast.error(t("generic_error"));
      }
    },
    [invoice, updateInvoice, onRemoveBooking, t],
  );

  const createdAt = new Date(invoice.createdAt);

  const formattedDate = createdAt.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const formattedTime = createdAt.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const handleReviewModeChange = () => {
    onReviewModeChange(
      reviewMode === ReviewModes.TASK ? ReviewModes.COMPLETE : ReviewModes.TASK,
    );
  };

  const isRetryExtractionDisabled = [
    InvoiceStatus.EXPORTED,
    InvoiceStatus.EXPORTING,
  ].includes(invoice.status);

  return (
    <>
      <div
        className={cn(
          "w-full rounded-lg px-4 py-2 flex flex-row items-center gap-2 mb-4 flex-wrap",
          invoice.status === InvoiceStatus.REVIEWED && "bg-[#27A544]",
          invoice.status === InvoiceStatus.EXPORTED && "bg-[#5f6ad2]",
          invoice.status === InvoiceStatus.ERROR ||
            (invoice.status === InvoiceStatus.EXPORT_FAILED &&
              "bg-[#eb5657] bg-opacity-90"),
          (invoice.status === InvoiceStatus.PREPROCESSED ||
            invoice.status === InvoiceStatus.NEW) &&
            "px-0 bg-background py-0",
        )}
      >
        <StatusSelect
          status={invoice.status}
          onSelect={handleUpdateStatus}
          disableDropdown
        >
          {({ icon }) => (
            <div className="flex bg-background items-center h-9 rounded-lg flex-row px-2">
              {icon}
            </div>
          )}
        </StatusSelect>
        {invoice.status !== InvoiceStatus.NEW ? (
          <div className="flex-1 min-w-[50px] overflow-hidden">
            <TypographySmall
              className={cn(
                "text-white font-semibold whitespace-nowrap overflow-hidden text-ellipsis block text-xs",
                invoice.status === InvoiceStatus.PREPROCESSED && "text-black",
              )}
            >
              {`${t("created_at")}: ${formattedDate} ${formattedTime}`}
            </TypographySmall>
          </div>
        ) : (
          <div className="flex-1 text-sm">
            <ProcessingStates invoiceId={invoice.invoiceId} />
          </div>
        )}
        <div className="flex flex-row gap-1">
          <div className="flex flex-row gap-1">
            <ActivityModeToggle
              showActivity={showActivity}
              setShowActivity={setShowActivity}
            />
          </div>
        </div>
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="gap-1"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("delete")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <InvoiceTypeDropdown
          currentType={invoice.type}
          isBooking={invoice.isBooking}
          onSelect={handleTypeSelect}
          disabled={disabled}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="flex h-9 w-9 p-0 data-[state=open]:bg-muted"
            >
              <EllipsisVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuItem
                    className="gap-2 data-[disabled]:pointer-events-auto"
                    onSelect={onRetryExtraction}
                    disabled={isRetryExtractionDisabled || disabled}
                  >
                    <RefreshCcw size={14} />
                    {t("retry_extraction.cta")}
                  </DropdownMenuItem>
                </TooltipTrigger>
                {isRetryExtractionDisabled && (
                  <TooltipContent>
                    {t("retry_extraction.disabled.tooltip")}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuItem
              className="gap-2"
              onSelect={handleReviewModeChange}
            >
              {reviewMode === ReviewModes.TASK ? (
                <Eye size={14} />
              ) : (
                <EyeOff size={14} />
              )}
              {reviewMode === ReviewModes.TASK
                ? t("review_mode.show")
                : t("review_mode.hide")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.history.back()}
                className="gap-1 border-none bg-transparent"
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="capitalize">{t("close")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {invoice.status === InvoiceStatus.ERROR && (
        <div className="mb-4">
          <InvoiceErrorAlert onRetry={onRetryExtraction} />
        </div>
      )}
      {invoice.status === InvoiceStatus.EXPORT_FAILED && (
        <div className=" mb-4">
          <InvoiceExportAlert invoice={invoice} />
        </div>
      )}
    </>
  );
};
